import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BarChart3, RefreshCw } from 'lucide-react';
import EmptyState from '../../components/ui/EmptyState';
import { outreachApi } from '../api/outreachApi';
import CampaignReport, { Metric, percent } from '../components/CampaignReport';
import '../outreach.css';

export default function AnalyticsPage() {
  const { t } = useTranslation('common');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await outreachApi.workspaceAnalytics());
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="unibox">
      <div className="toolbar">
        <h1 className="toolbar-title">{t('outreach.analytics.title')}</h1>
        <div className="toolbar-filters" />
        <div className="toolbar-actions">
          <button className="unibox-refresh" onClick={load}>
            <RefreshCw size={14} /> {t('outreach.refresh')}
          </button>
        </div>
      </div>

      {error && <div className="unibox-banner">{String(error)}</div>}
      {loading && <div className="unibox-empty">{t('outreach.loading')}</div>}

      {!loading && data && data.campaigns.length === 0 && (
        <EmptyState
          icon={<BarChart3 size={28} />}
          title={t('outreach.analytics.empty')}
          text={t('outreach.analytics.emptyHint')}
        >
          <Link className="unibox-button" to="/outreach/campaigns">
            {t('outreach.campaign.create')}
          </Link>
        </EmptyState>
      )}

      {!loading && data && data.campaigns.length > 0 && (
        <div className="analytics-body">
          <div className="metric-row">
            <Metric
              label={t('outreach.analytics.replyRate')}
              value={percent(data.reply_rate)}
              hint={t('outreach.analytics.hints.replyRate')}
              tone="primary"
            />
            <Metric label={t('outreach.analytics.sent')} value={data.totals.sent} />
            <Metric label={t('outreach.analytics.delivered')} value={data.totals.delivered} />
            <Metric label={t('outreach.analytics.replies')} value={data.totals.replies} />
            <Metric
              label={t('outreach.analytics.autoReplies')}
              value={data.totals.auto_replies}
              hint={t('outreach.analytics.hints.autoReplies')}
            />
            <Metric
              label={t('outreach.analytics.bounced')}
              value={data.totals.bounced_hard}
              hint={t('outreach.analytics.hints.bounced')}
              tone={data.totals.bounced_hard > 0 ? 'alert' : undefined}
            />
            <Metric label={t('outreach.analytics.unsubscribed')} value={data.totals.unsubscribed} />
          </div>

          {data.campaigns.map((report) => (
            <CampaignReport key={report.campaign_id} report={report} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}
